import { z } from 'zod'
import { IsoTimestamp, MicroString, UsdString, Uuid } from '../primitives.js'
import { AgentStatus, InboxKind, InboxStatus } from '../enums.js'
import { AgentProfile, AgentVersionSummary, ModelPolicy } from './agents.js'
import { ApiKey } from './identity.js'
import { CreditEnvelope } from './billing.js'
import { JsonObject } from './common.js'

export const InboxItem = z.object({
  id: Uuid,
  kind: InboxKind,
  payload: JsonObject,
  status: InboxStatus,
  createdAt: IsoTimestamp,
  processedAt: IsoTimestamp.nullable(),
})
export type InboxItem = z.output<typeof InboxItem>

/** GET /agents/:handle/manage (owner only). */
export const AgentManage = z.object({
  agent: AgentProfile,
  modelPolicy: ModelPolicy.nullable(),
  versions: z.array(AgentVersionSummary),
  envelopes: z.array(CreditEnvelope),
  usage24h: z.object({ calls: z.number().int().nonnegative(), chargedMicro: MicroString }),
  inbox: z.array(InboxItem),
  apiKeys: z.array(ApiKey),
  /** Active and paused customer permissions naming this agent. */
  permissionsCount: z.number().int().nonnegative(),
  subscribersCount: z.number().int().nonnegative(),
  feesCollectedUsd: UsdString,
  /** Registered in AgentRegistry (`agent.provision`). */
  registered: z.boolean(),
})
export type AgentManage = z.output<typeof AgentManage>

/** GET /agent/me (agent key). */
export const AgentSelf = z.object({
  agent: AgentProfile,
  version: AgentVersionSummary.nullable(),
  permissions: z.object({ active: z.number().int().nonnegative(), paused: z.number().int().nonnegative() }),
})
export type AgentSelf = z.output<typeof AgentSelf>


export const AgentStatusResult = z.object({ status: AgentStatus })

/**
 * One on-chain operation the platform's operator account carries for the agent (lane ALGOBOO1): its registration,
 * token launch, a fee period, a fee forward, an epoch's sweep or its retirement, each step with every attempt. A failed
 * one names why and whether it can be sent again (`retryable`: never while a step's outcome is unknown -- it may still
 * land, the platform's operator looks first).
 */
export const AgentOperation = z.object({
  operationRef: z.string().regex(/^opn_[0-9a-z]{16,40}$/),
  kind: z.enum(['register', 'launch', 'fee_period', 'forward_fees', 'refund', 'retire', 'sweep']),
  state: z.enum(['planned', 'running', 'done', 'failed']),
  refusalCode: z.string().nullable(),
  retried: z.boolean(),
  retryable: z.boolean(),
  /** What happened, in plain words. */
  words: z.string(),
  steps: z.array(
    z.object({
      ordinal: z.number().int().positive(),
      attempt: z.number().int().positive(),
      function: z.string(),
      state: z.string(),
      reason: z.string().nullable(),
      txHash: z.string().nullable(),
    }),
  ),
  requestedAt: IsoTimestamp,
  settledAt: IsoTimestamp.nullable(),
})
export type AgentOperation = z.output<typeof AgentOperation>

/** The agent's operations on the platform, newest first; `unavailable` says why when they cannot be read. */
export const AgentOperations = z.object({
  platformState: z.string().nullable(),
  operations: z.array(AgentOperation),
  unavailable: z.string().nullable(),
})
export type AgentOperations = z.output<typeof AgentOperations>

// Admin --------------------------------------------------------------------------

export const UpsertRateCard = z.object({
  brainId: z.string(),
  inputMicroPer1k: MicroString,
  outputMicroPer1k: MicroString,
  markupBps: z.number().int().min(0).max(100_000),
  effectiveFrom: IsoTimestamp.optional(),
})
export type UpsertRateCard = z.input<typeof UpsertRateCard>

export const ModerateAgent = z.object({ status: z.enum(['active', 'paused', 'retired']), reason: z.string().min(1) })
export type ModerateAgent = z.input<typeof ModerateAgent>

export const JobEnqueued = z.object({ jobId: Uuid })
export type JobEnqueued = z.output<typeof JobEnqueued>
